import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import Inputmask from "inputmask";
import { Eye, EyeOff, Loader, Lock, Save } from "lucide-react";
import Spacing from "../Spacing";
import DropdownWithSearch from "../DropdownWithSearch";
import { can_visit, getPractice, usPhoneFormat, validatePassword } from "../../helpers";
import { useSelector } from "react-redux";
import { Drawer } from 'vaul';

export default function AddUserModal({
  allProviders = [],
  openModal = false,
  setOpenModal = () => { },
  account_id,
  location_id,
  userRoles = [],
  handleAddUserInfo = () => { },
  addUserLoader = false,
  previousScrollPosition = 0
}) {
  const userProfileData = useSelector((state) => state.practice.userProfile);

  const [addUserData, setAddUserData] = useState({
    account_id: account_id,
    location_id: location_id,
    first_name: "",
    last_name: "",
    contact: "",
    user_name: "",
    password: "",
    recipient_email: "",
    roles: [],
    groups: [],
    speciality_ids: [],
    is_provider: false,
    ehr_type: "",
    ehr_username: "",
    ehr_password: "",
    linked_providers: [],
    emr_provider_id: "",
    default_location_id: "",
    additional_permissions: []
  });

  const [formErrors, setFormErrors] = useState({
    roles: false,
    groups: false,
  });

  const [isValid, setIsValid] = useState(true);
  const [passType, setPassType] = useState("password");
  const [allSpeciality, setAllSpeciality] = useState([]);
  const dropdownRef = useRef(null);
  const [ehrPassword, setEhrPassword] = useState(false);
  const [allProvidersOptions, setAllProvidersOptions] = useState([]);
  const [ehrType] = useState([
    { alue: 'ECW', label: 'ECW', },
    { value: 'MDLand', label: 'MDLand', },
    { value: 'VeradigmEHR', label: 'VeradigmEHR', },
    { value: 'AthenaHealth', label: 'AthenaHealth', }
  ]);
  const additionalPermissionsOptions = [
    { value: 'charting', label: 'Charting' },
    { value: 'billing', label: 'Billing' },
    { value: 'coding', label: 'Coding' },
  ];

  const getAllSpeciality = async () => {
    try {
      const response = getPractice("specialities");
      if (response?.length) {
        const allSpecialities = response?.map((option) => ({
          value: option?.id,
          label: option?.name,
        }))
        setAllSpeciality(allSpecialities);
      };
    } catch (error) {
    }
  };

  useEffect(() => {
    getAllSpeciality();
  }, []);

  useEffect(() => {
    if (addUserData.roles.length > 0 && formErrors.roles) {
      setFormErrors((prev) => ({ ...prev, roles: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUserData.roles]);

  useEffect(() => {
    if (addUserData.groups.length > 0 && formErrors.groups) {
      setFormErrors((prev) => ({ ...prev, groups: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUserData.groups]);

  const getAllProviders = () => {
    try {

      if (allProviders?.length) {
        const allProvidersFormatted = allProviders?.map((option) => ({
          value: option?.provider_id,
          label: `${option?.first_name} ${option?.last_name}`,
        }))
        setAllProvidersOptions(allProvidersFormatted);
      };
    } catch (error) {
      // console.log("console.log("🚀 ~ getAllProviders: ~ error:", error);
    }
  };

  useEffect(() => {
    getAllProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSpecialityChange = (selectedOption) => {
    const selectedValues = selectedOption ? selectedOption.map((option) => option.value) : [];

    setAddUserData({
      ...addUserData,
      speciality_ids: selectedValues,
    });
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setAddUserData({
      ...addUserData,
      'password': value
    });
    setIsValid(validatePassword(value));
  };

  const handleChangeRoles = (value) => {
    setAddUserData({
      ...addUserData,
      roles: addUserData.roles.includes(value)
        ? addUserData.roles.filter((role) => role !== value)
        : [...addUserData.roles, value],
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "contact") {
      const phoneInputs = document.querySelectorAll(".us_phone");
      Inputmask({
        mask: "(999) 999-9999",
        showMaskOnHover: false,
        showMaskOnFocus: true,
      }).mask(phoneInputs);
    }
    setAddUserData({
      ...addUserData,
      [name]: value
    });
  };

  const handleEhrPasswordChange = (e) => {
    const { name, value } = e.target;
    setAddUserData({
      ...addUserData,
      [name]: value
    });
  };

  const handleLinkedProvidersChange = (selectedOption) => {
    const selectedValues = selectedOption ? selectedOption?.map((option) => option?.value) : [];
    setAddUserData({
      ...addUserData,
      linked_providers: selectedValues,
    });
  };

  const handleSelectGroup = (account) => {
    const accountKey = `/${account?.practices[0]?.account_name}`;
    const practiceKeys = account?.practices?.map((practice) => `/${account?.practices[0]?.account_name}/${practice?.name}`);

    setAddUserData((prevData) => {
      const isAccountSelected = prevData?.groups?.includes(accountKey);
      const nextGroups = prevData?.groups?.filter(
        (group) => group !== accountKey && !practiceKeys?.includes(group)
      );

      const remainingLocationIds = new Set();
      (userProfileData?.accounts || []).forEach((acc) => {
        (acc?.practices || []).forEach((p) => {
          const key = `/${acc?.practices[0]?.account_name}/${p?.name}`;
          if (nextGroups.includes(key)) remainingLocationIds.add(String(p?.location_id));
        });
      });
      const keepDefault = remainingLocationIds.has(String(prevData?.default_location_id)) ? prevData?.default_location_id : "";

      if (isAccountSelected) {
        return {
          ...prevData,
          groups: nextGroups,
          default_location_id: keepDefault
        };
      }

      return {
        ...prevData,
        groups: [...new Set([...prevData.groups || [], accountKey, ...practiceKeys])],
      };
    });
  };

  const handleChangePractices = (practiceName, accountName, location_id) => {
    const practiceKey = `/${accountName}/${practiceName}`;
    const accountKey = `/${accountName}`;

    setAddUserData((prevData) => {
      const isPracticeSelected = prevData?.groups?.includes(practiceKey);

      if (isPracticeSelected) {
        const updatedGroups = prevData?.groups?.filter((group) => group !== practiceKey);

        const hasRemainingPractices = updatedGroups?.some((group) =>
          group?.startsWith(`${accountKey}/`)
        );
        const nextDefault = String(prevData?.default_location_id) === String(location_id) ? "" : prevData?.default_location_id;

        return {
          ...prevData,
          groups: hasRemainingPractices
            ? updatedGroups
            : updatedGroups?.filter((group) => group !== accountKey),
          default_location_id: nextDefault
        };
      }

      return {
        ...prevData,
        groups: [...new Set([...(prevData.groups || []), practiceKey, accountKey])],
      };
    });
  };

  const handleIsProviderChange = async (e) => {
    const value = !addUserData.is_provider;
    setAddUserData({ ...addUserData, is_provider: value });
  };

  const generatePassword = () => {
    const length = 12;
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const specials = "!@#$%^&*()_+~";
    const allChars = lower + upper + numbers + specials;

    let password = "";

    // ensure at least one of each:
    password += lower[Math.floor(Math.random() * lower.length)];
    password += upper[Math.floor(Math.random() * upper.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += specials[Math.floor(Math.random() * specials.length)];

    // fill the rest:
    for (let i = password.length; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * allChars.length);
      password += allChars[randomIndex];
    }

    // shuffle to mix required chars
    password = password
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");

    setAddUserData({
      ...addUserData,
      password,
    });
    setIsValid(validatePassword(password));
  };

  const updatePracticeHTML = () => {
    let seletedValues = addUserData.groups;
    let multiple_select = document.querySelector(".multiple_select .docpro_dropdown_with_search_btn");
    if (multiple_select) {
      if (seletedValues.length === 0) {
        multiple_select.innerHTML = 'Select Practices <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-down "><path d="m6 9 6 6 6-6"></path></svg>';
      } else {
        seletedValues.sort();
        let htmlString = seletedValues
          .map((item) => `<span>${item}</span>`)
          .join(", ");
        multiple_select.innerHTML = htmlString;
      }
    }
  };

  useEffect(() => {
    updatePracticeHTML();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUserData.groups]);

  const handleDrawerClose = () => {
    setOpenModal(false);
    window.scrollTo(0, previousScrollPosition);
  };


  // Build selected practice options with location_id as value
  const selectedPracticeOptions = (() => {
    const options = [];
    (userProfileData?.accounts || []).forEach((account) => {
      (account?.practices || []).forEach((practice) => {
        const practiceKey = `/${account?.practices[0]?.account_name}/${practice?.name}`;
        if (addUserData?.groups?.includes(practiceKey)) {
          options.push({ value: practice?.location_id, label: practice?.name });
        }
      });
    });
    return options;
  })();

  useEffect(() => {
    if (!addUserData?.default_location_id) return;
    const stillSelected = selectedPracticeOptions.some(
      (o) => o.value === addUserData.default_location_id
    );
    if (!stillSelected) {
      setAddUserData((prev) => ({ ...prev, default_location_id: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUserData.groups, userProfileData]);

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={openModal} onOpenChange={handleDrawerClose}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay" />
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4">
            <Drawer.Title className="sr-only">Add User</Drawer.Title>
            <Drawer.Description className="sr-only">Add a new user</Drawer.Description>
            <div className="docpro_modal_wrap docpro_modal_lg docpro_modal_lg2 center">
              <div className="docpro_modal">
                <h2 className="docpro_fs_24">Add User</h2>

                <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_3">
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      Last Name <span className="docpro_red_color">*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      defaultValue={addUserData.last_name || ""}
                      className="docpro_form_field size_lg"
                      autoComplete="off"
                      onChange={(e) => { handleChange(e) }}
                      placeholder="Last Name"
                    />
                  </div>
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      First Name <span className="docpro_red_color">*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      defaultValue={addUserData.first_name || ""}
                      className="docpro_form_field size_lg"
                      autoComplete="off"
                      onChange={(e) => { handleChange(e) }}
                      placeholder="First Name"
                    />
                  </div>
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      Email <span className="docpro_red_color">*</span>
                    </label>
                    <input
                      type="email"
                      name="recipient_email"
                      required
                      defaultValue={addUserData.recipient_email || ""}
                      className="docpro_form_field size_lg"
                      autoComplete="off"
                      onChange={(e) => { handleChange(e) }}
                      placeholder="Email"
                    />
                  </div>
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">Contact</label>
                    <input
                      type="text"
                      className="docpro_form_field size_lg us_phone"
                      name="contact"
                      autoComplete="off"
                      onChange={(e) => { handleChange(e) }}
                      defaultValue={addUserData.contact ? usPhoneFormat(addUserData.contact) : ""}
                      maxLength={20}
                      placeholder="(123) 123 1234"
                    />
                  </div>
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      User Name <span className="docpro_red_color">*</span>
                    </label>
                    <input
                      type="text"
                      name="user_name"
                      required
                      defaultValue={addUserData.user_name || ""}
                      className="docpro_form_field size_lg"
                      autoComplete="off"
                      onChange={(e) => { handleChange(e) }}
                      placeholder="Username"
                    />
                  </div>
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      Password <span className="docpro_red_color">*</span>
                    </label>
                    <div className='flex items-center'>
                      <div className="docpro_form_field_with_icon w-full">
                        <input
                          type={passType}
                          className={`docpro_form_field size_lg ${!isValid && 'docpro_border_red'}`}
                          name="password"
                          required
                          placeholder="••••••••"
                          value={addUserData?.password}
                          minLength={8}
                          onChange={handlePasswordChange}
                          style={{ borderRadius: "8px 0 0 8px", padding: '5px 5px 5px 36px' }}
                        />
                        <Lock
                          width={18}
                          height={18}
                          className="docpro_form_field_icon"
                        />
                      </div>
                      <div className='flex items-center'>
                        <div className='pointer' style={{ padding: '7px 12px', height: '40px', backgroundColor: '#c0c0c0' }}>
                          {passType === "text" ?
                            <Eye
                              width={18}
                              height={18}
                              color='var(--secondary)'
                              onClick={() => { setPassType("password") }}
                            />
                            :
                            <EyeOff
                              width={18}
                              height={18}
                              color='var(--secondary)'
                              onClick={() => { setPassType("text") }}
                            />
                          }
                        </div>
                        <button type='button'
                          className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                          onClick={generatePassword}
                          style={{ height: '40px', borderRadius: "0 8px 8px 0", outline: 0 }}
                        >Generate</button>
                      </div>
                    </div>
                  </div>
                </div>
                {(!isValid) && (
                  <p className="text-red-500 text-sm mb-0 mt-1">
                    Password must be at least 12 characters and include uppercase, lowercase, number, and special character.
                  </p>
                )}
                <Spacing lg="16" sm="16" />

                <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_3">
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      Role <span className="docpro_red_color">*</span>
                    </label>
                    <DropdownWithSearch
                      placeholder={
                        addUserData.roles.length
                          ? `${addUserData.roles.join(", ")}`
                          : "Select Roles"
                      }
                    >
                      <ul className="docpro_checkmark_list">
                        {(can_visit("administrator") ? userRoles : userRoles?.filter(
                          (role) => !["Trial", "System", "Enterprise"].includes(role)
                        ))?.map((item, index) => (
                          <li key={index}>
                            <div className="docpro_checkbox" style={{ top: "3px" }}>
                              <input
                                type="checkbox"
                                checked={addUserData.roles.includes(item)}
                                onChange={() => handleChangeRoles(item)}
                              />
                              <label>
                                <span style={{ marginLeft: "8px" }}>{item}</span>
                              </label>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </DropdownWithSearch>
                    {formErrors.roles && (
                      <p className="text-red-500 text-sm mt-1">Please select at least one role.</p>
                    )}
                  </div>

                  {allSpeciality?.length > 0 && (
                    <div className="docpro_grid_item">
                      <label className="docpro_fw_500 docpro_primary_color">
                        Specialities
                      </label>
                      <Select
                        isMulti
                        name="speciality_ids"
                        options={allSpeciality}
                        placeholder="Search..."
                        isSearchable
                        value={allSpeciality?.filter((option) =>
                          addUserData.speciality_ids?.includes(option.value)
                        ) || []}
                        onChange={(selectedOption) => handleSpecialityChange(selectedOption)}
                        className="react-select-container w-full"
                        classNamePrefix="react-select"
                        filterOption={(option, rawInput) => {
                          const words = rawInput?.toLowerCase()?.split(" ");
                          return words?.every((word) =>
                            option?.label?.toLowerCase()?.includes(word)
                          );
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '40px',
                            borderColor: 'var(--border)',
                            borderRadius: '8px'
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1050,
                            marginTop: '-2px',
                            borderRadius: '0 0 3px 3px',
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1050,
                            padding: 0
                          }),
                        }}
                      />
                    </div>
                  )}

                  <div className="docpro_grid_item flex items-center">
                    <div className="flex items-center">
                      <label className="docpro_fw_500 docpro_primary_color docpro_mb_0 mr-2">
                        Are You a Provider?:
                      </label>
                      <label className="switch switch_mode mb-0">
                        <input
                          name="is_provider"
                          checked={addUserData?.is_provider || false}
                          onChange={handleIsProviderChange}
                          type="checkbox"
                          className="docpro_logo_color"
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>

                  {/* additional permissions */}
                  {can_visit("administrator") &&
                    <div className="docpro_grid_item">
                      <label className="docpro_fw_500 docpro_primary_color">
                        Additional Permissions
                      </label>

                      <Select
                        isMulti
                        options={additionalPermissionsOptions}
                        value={additionalPermissionsOptions.filter(opt =>
                          addUserData.additional_permissions.includes(opt.value)
                        )}
                        onChange={(selectedOptions) => {
                          setAddUserData({
                            ...addUserData,
                            additional_permissions: selectedOptions
                              ? selectedOptions.map(opt => opt.value)
                              : [],
                          });
                        }}
                        placeholder="Select Additional Permissions"
                        className="react-select-container w-full"
                        classNamePrefix="react-select"
                      />
                    </div>
                  }

                  {can_visit('ehr_veradigm') && (
                    <>
                      <div className="docpro_grid_item">
                        <label className="docpro_fw_500 docpro_primary_color"> EHR Type </label>
                        <Select
                          name="ehr_type"
                          options={ehrType}
                          onChange={(selectedOption) => {
                            setAddUserData({ ...addUserData, ehr_type: selectedOption.value });
                          }}
                          placeholder="Search..."
                          isSearchable={true}
                          value={ehrType.find(
                            (option) => option.value === addUserData.ehr_type
                          )}
                          className="react-select-container w-full"
                          classNamePrefix="react-select"
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '40px',
                              borderRadius: '8px',
                              borderColor: 'var(--border)',
                            }),
                          }}
                        />
                      </div>
                      <div className="docpro_grid_item">
                        <label className="docpro_fw_500 docpro_primary_color"> EHR Username </label>
                        <input
                          type="text"
                          className="docpro_form_field size_lg"
                          name="ehr_username"
                          autoComplete="off"
                          onChange={(e) => { handleChange(e) }}
                          defaultValue={addUserData.ehr_username}
                        />
                      </div>
                      <div className="docpro_grid_item">
                        <label className="docpro_fw_500 docpro_primary_color"> EHR Password </label>
                        <div className="docpro_form_field_with_icon">
                          <input
                            name="ehr_password"
                            type={ehrPassword ? "text" : "password"}
                            placeholder="••••••••"
                            className={`docpro_form_field size_lg`}
                            onChange={handleEhrPasswordChange}
                            autoComplete="new-password"
                            minLength={4}
                          />
                          <Lock width={18} height={18} className="docpro_form_field_icon" />
                          {ehrPassword ?
                            <Eye onClick={() => setEhrPassword(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                            :
                            <EyeOff onClick={() => setEhrPassword(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                          }
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <Spacing lg="16" sm="16" />
                <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_1_3">
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      EMR Provider ID
                    </label>
                    <input
                      type="text"
                      name="emr_provider_id"
                      defaultValue={addUserData.emr_provider_id || ""}
                      className="docpro_form_field size_lg"
                      autoComplete="off"
                      onChange={(e) => { handleChange(e) }}
                      placeholder="Provider ID"
                    />
                  </div>
                  {allProvidersOptions?.length > 0 && (
                    <div className="docpro_grid_item">
                      <label className="docpro_fw_500 docpro_primary_color">
                        Linked Providers
                      </label>
                      <Select
                        isMulti
                        name="linked_providers"
                        options={allProvidersOptions}
                        placeholder="Search..."
                        isSearchable
                        value={allProvidersOptions?.filter((option) =>
                          addUserData?.linked_providers?.includes(option?.value)
                        ) || []}
                        onChange={(selectedOption) => handleLinkedProvidersChange(selectedOption)}
                        className="react-select-container w-full"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '40px',
                            borderColor: 'var(--border)',
                            borderRadius: '8px'
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1050,
                            marginTop: '-2px',
                            borderRadius: '0 0 3px 3px',
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1050,
                            padding: 0
                          }),
                        }}
                      />
                    </div>
                  )}
                </div>

                <Spacing lg="16" sm="16" />
                <div className="docpro_grid_item multiple_select">
                  <label className="docpro_fw_500 docpro_primary_color">
                    Practices <span className="docpro_red_color">*</span>
                  </label>
                  <DropdownWithSearch
                    placeholder={
                      addUserData?.groups?.length
                        ? `${addUserData?.groups?.join(", ")}`
                        : "Select Practices"
                    }
                  >
                    {userProfileData?.accounts?.map((account, index) => (
                      <div key={index}>
                        <li style={{ listStyle: "none", margin: "10px 0px" }}>
                          <div
                            className="docpro_checkbox"
                            style={{ top: "3px" }}
                          >
                            <input
                              type="checkbox"
                              onChange={() => handleSelectGroup(account)}
                              checked={addUserData?.groups?.includes(`/${account?.practices[0]?.account_name}`)}
                            />
                            <label>
                              <span style={{ marginLeft: "8px" }}>
                                {account?.practices[0]?.account_name}
                              </span>
                            </label>
                          </div>
                        </li>
                        {account?.practices?.map((practice) => {
                          const practiceKey = `/${account?.practices[0]?.account_name}/${practice?.name}`;
                          return (
                            <li
                              key={practice.location_id}
                              style={{ listStyle: "none", paddingLeft: "15px" }}
                            >
                              <div
                                className="docpro_checkbox"
                                style={{ top: "3px" }}
                              >
                                <input
                                  type="checkbox"
                                  onChange={() => handleChangePractices(practice?.name, account?.practices[0]?.account_name, practice?.location_id)}
                                  checked={addUserData?.groups?.includes(practiceKey)}
                                />
                                <label>
                                  <span style={{ marginLeft: "8px" }}>
                                    {practice?.name}
                                  </span>
                                </label>
                              </div>
                            </li>
                          )
                        })}
                      </div>
                    ))}
                  </DropdownWithSearch>
                  {formErrors.groups && (
                    <p className="text-red-500 text-sm mt-1">Please select at least one practice.</p>
                  )}
                </div>

                <Spacing lg="16" sm="16" />
                <div className="docpro_grid_item">
                  <label className="docpro_fw_500 docpro_primary_color">
                    Default Location
                  </label>
                  <Select
                    name="default_location_id"
                    placeholder={selectedPracticeOptions?.length
                      ? "Select a default location"
                      : "Select practices first"
                    }
                    isSearchable
                    isClearable
                    options={selectedPracticeOptions}
                    value={addUserData?.default_location_id
                      ? selectedPracticeOptions.find(o => o.value === addUserData.default_location_id) || null
                      : null
                    }
                    onChange={(opt) => {
                      setAddUserData(prev => ({
                        ...prev,
                        default_location_id: opt?.value || ""
                      }));
                    }}
                    className="react-select-container w-full"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '40px',
                        borderColor: 'var(--border)',
                        borderRadius: '8px'
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 1050,
                        marginTop: '-2px',
                        borderRadius: '0 0 3px 3px',
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 1050, padding: 0 }),
                    }}
                  />
                </div>

                <Spacing lg="30" sm="30" />
                <div className="docpro_modal_btns small_buttons docpro_mt_auto">
                  <button
                    className="docpro_bordered_btn docpro_fw_600 size_lg"
                    onClick={() => { handleDrawerClose(); }}
                  >
                    Cancel
                  </button>
                  <button
                    className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                    onClick={() => {
                      const hasValidPassword = validatePassword(addUserData?.password);
                      const hasRoles = addUserData?.roles?.length > 0;
                      const hasGroups = addUserData?.groups?.length > 0;

                      const newErrors = {
                        roles: !hasRoles,
                        groups: !hasGroups,
                      };

                      setFormErrors(newErrors);

                      if (!hasValidPassword) {
                        setIsValid(false);
                        return;
                      }

                      if (!hasRoles || !hasGroups) {
                        return; // prevent submit
                      }

                      handleAddUserInfo({ ...addUserData });
                    }}
                    disabled={addUserLoader ? true : false}
                  >
                    <Save width={20} height={20} />
                    <span>Save</span>
                    {addUserLoader && (
                      <span className="docpro_loading">
                        <Loader width={20} height={20} />
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
